'use client';
import { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { dateToSimpleDate, type GameType } from '@puzzlio/core';
import { Button } from '@puzzlio/ui';
import { cn } from '@/app/lib/utils';
import { GAME_CONFIG, PLAYABLE_GAME_TYPES } from '@/app/games/config';
import { useIntroAwareGameURL } from '@/app/hooks/useIntroAwareGameURL';
import { useWeekBoardsAndPlays } from '@/app/hooks/gameBoardAndPlayHooks';
import { CARDS } from './HomeGameCards';

/*
  The closing nudge for a newcomer, dressed as one of today's puzzles they haven't solved yet, picked at
  random; gone once they've solved them all, and promising their "next" puzzle rather than their "first"
  once they've solved any. Only open games are candidates: this is shown signed out, and a game behind
  an account wall is no first puzzle.
*/
export function ReadyToPlaySection({ className }: { className?: string }) {
  const today = dateToSimpleDate(new Date());
  const { data, isError } = useWeekBoardsAndPlays(today, today);
  // Drawn once, so the pick only changes if the set of unsolved games does.
  const [draw] = useState(Math.random);

  const { unsolved, solvedAny } = useMemo(() => {
    const open = PLAYABLE_GAME_TYPES.filter((game) => GAME_CONFIG[game].availability === 'available');
    // Until the plays have synced there's nothing to go on; if they never do, nothing is known to be solved.
    if (!data && !isError) return { unsolved: [], solvedAny: false };
    const solved = (game: GameType) =>
      !!data?.[game].some((day) => day.date === today && day.playStatus === 'complete');
    return { unsolved: open.filter((game) => !solved(game)), solvedAny: open.some(solved) };
  }, [data, isError, today]);

  if (!unsolved.length) return null;
  return <ReadyCard game={unsolved[Math.floor(draw * unsolved.length)]} solvedAny={solvedAny} className={className} />;
}

function ReadyCard({ game, solvedAny, className }: { game: GameType; solvedAny: boolean; className?: string }) {
  const { fill, art } = CARDS[game];
  // The same destination as the game's own card: its tutorial until the player has graduated.
  const href = useIntroAwareGameURL(game);

  return (
    <section
      className={cn(
        'flex flex-col items-center gap-6 px-6 py-9 md:flex-row md:gap-10 md:rounded-xl md:px-10 md:py-7',
        fill,
        className
      )}
    >
      {/* The pieces differ in shape, so each fits a box rather than sharing a width. */}
      <Image
        src={art}
        alt=""
        className="h-[160px] w-auto max-w-[260px] shrink-0 object-contain md:h-[140px] md:max-w-[220px]"
      />
      <div className="flex w-full flex-col items-center md:items-start">
        <h2 className="font-chinook leading-display text-center text-3xl md:text-left">Ready when you are!</h2>
        <p className="font-quicksand mt-1 text-lg leading-snug font-semibold">
          Your {solvedAny ? 'next' : 'first'} puzzle is waiting…
        </p>
        <Button asChild variant="gameplay" size="gameplay" className="mt-4 w-full max-w-[320px] px-4 md:w-auto">
          <Link prefetch={false} href={href}>
            Start Playing
          </Link>
        </Button>
      </div>
    </section>
  );
}
